import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@mui/material'
import BarbeibotQuestionWithAttachment from '../../../components/AIChat/BarbeibotQuestionWithAttachment'
import { useNotification } from '../../../context/NotificationManager/NotificationManager'
import { LoadingSpinner } from '../../../components/PageLoading/PageLoading'
import MarkdownContent from '../../../components/MarkdownContent/MarkdownContent'
import avatar from './barbeibot.png'
import { preguntaAvanzada } from '../../../utils/api/barbeibot'
import css from './Consulta.module.css'

const MAX_SIZE = 10 * 1024 * 1024

const Consulta = () => {
  const notification = useNotification()
  const [respuesta, setRespuesta] = useState(null)

  const { isPending: isAsking, mutate: ask } = useMutation({
    mutationFn: preguntaAvanzada,
    onSuccess: respuesta => {
      setRespuesta(respuesta)
    },
    onError: err => {
      notification.error({
        title: 'Error al preguntar al Barbeibot',
        content: err
      })
    }
  })

  const handleSubmit = ({ question, files, model }) => {
    if (isAsking) return

    if (files && files.length > 0) {
      const oversizedFile = files.find(file => file.size > MAX_SIZE)
      if (oversizedFile) {
        notification.error({
          title: 'Archivo demasiado grande',
          content: `El archivo "${oversizedFile.name}" excede el tamaño máximo de 10MB`
        })
        return
      }
    }

    const payload = {
      pregunta: question,
      model
    }

    if (files && files.length > 0) {
      payload.images = files.filter(file => file.type.startsWith('image/'))
      payload.archivos = files.filter(file => !file.type.startsWith('image/'))
    }

    ask(payload)
  }

  const handleReset = () => {
    setRespuesta(null)
  }

  const isPregunta = !respuesta && !isAsking
  const isRespuesta = respuesta && !!respuesta.respuesta
  const isRespuestaVacia =
    respuesta &&
    !isAsking &&
    (respuesta.respuesta === null || respuesta.respuesta === '')

  return (
    <>
      {isPregunta && (
        <div className={css.main}>
          <img src={avatar} alt='Barbeibot' className={css.avatar} />
          <div className={css.formWrapper}>
            <BarbeibotQuestionWithAttachment
              title='Pregúntale a Barbeibot lo que quieras, lo sabe todo'
              subtitle='Puedes adjuntar archivos o imágenes para obtener respuestas más precisas'
              onSubmit={handleSubmit}
              isLoading={isAsking}
              accept='image/*,.pdf,.doc,.docx,.txt'
            />
          </div>
        </div>
      )}
      {isAsking && (
        <div className={css.loading}>
          <div className={css.thought}>
            <div className={css.bubble}>
              Analizando tu consulta... Dame un momento para procesar todo este
              conocimiento.
            </div>
            <img className={css.avatar} src={avatar} alt='Barbeibot' />
          </div>
          <LoadingSpinner className={css.spinner} />
        </div>
      )}
      {isRespuesta && (
        <div className={css.answer}>
          <div className={css.response}>
            <MarkdownContent content={respuesta.respuesta} />
          </div>
          <div className={css.answerActions}>
            <div className={css.buttons}>
              <Button
                variant='outlined'
                color='secondary'
                onClick={handleReset}
              >
                Hacer nueva consulta
              </Button>
            </div>
            <img className={css.responseAvatar} src={avatar} alt='Barbeibot' />
          </div>
        </div>
      )}
      {isRespuestaVacia && (
        <div className={css.answer}>
          <div className={css.response}>
            No se pudo generar una respuesta para esta consulta. Vuelve a
            realizar la pregunta usando otro modelo.
          </div>
          <div className={css.answerActions}>
            <div className={css.buttons}>
              <Button
                variant='outlined'
                color='secondary'
                onClick={handleReset}
              >
                Hacer nueva consulta
              </Button>
            </div>
            <img className={css.responseAvatar} src={avatar} alt='Barbeibot' />
          </div>
        </div>
      )}
    </>
  )
}

export default Consulta
